import type { ReactNode } from 'react';
import { Spinner } from '@dpem/design-system';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/app/providers/AuthProvider';
import { PATHS, REDIRECT_PARAM } from '@/router/paths';

/**
 * Gate every authenticated route on a restored session.
 *
 * Three states, three answers. `restoring` renders a spinner — redirecting here
 * would bounce every reload through the login screen and lose the deep link.
 * `anonymous` redirects to sign in *with the attempted path*, so the user lands
 * where they were going. `authenticated` renders.
 *
 * `replace` matters: without it, back from the login screen returns to the guarded
 * route, which redirects to login again — a loop the user cannot escape with the
 * back button.
 *
 * This is not a security boundary. Every request the surfaces make is authorised
 * independently by the API; this only keeps a signed-out visitor from watching a
 * surface fail one request at a time.
 */

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  // `Spinner` already carries `role="status"` and its own announced label, so it is
  // returned bare — a wrapper with a second `role="status"` announces twice.
  if (status === 'restoring') return <Spinner size="md" label="Restoring your session" />;

  if (status === 'anonymous') {
    // Search and hash go along with the path: an explorer deep link carries the
    // selected element in the query, and dropping it would land the user on a bare
    // graph after signing in.
    const attempted = `${location.pathname}${location.search}${location.hash}`;
    const next =
      attempted === PATHS.root
        ? PATHS.login
        : `${PATHS.login}?${REDIRECT_PARAM}=${encodeURIComponent(attempted)}`;
    return <Navigate to={next} replace />;
  }

  return <>{children}</>;
}
